import React, { useEffect, useState } from 'react';

import VectorIcon from 'assets/actionPlan/vector.svg';
import Button from 'common-ui/button';
import Input from 'common-ui/input';
import Modal from 'common-ui/modal';
import TextArea from 'common-ui/textArea';

import {
  AlertIcon,
  ButtonWrapper,
  DualInputWrapper,
  ErrorForInputs,
  ErrorMessage,
  FormWrapper,
  InputSection,
} from './EditPresValue.styles';

const EditPresValue = ({
  isOpen,
  onClose,
  onSubmit,
  estimationType,
  initialQuantityValue = '',
  initialFinancialValue = '',
  initialComment = '',
  loading = false,
}) => {
  const [quantityValue, setQuantityValue] = useState('');
  const [financialValue, setFinancialValue] = useState('');
  const [comment, setComment] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      setQuantityValue(initialQuantityValue?.toString() || '');
      setFinancialValue(initialFinancialValue?.toString() || '');
      setComment(initialComment || '');
      setErrors({});
    }
  }, [isOpen, initialQuantityValue, initialFinancialValue, initialComment]);

  const handleSubmit = () => {
    const newErrors = {};

    if (estimationType === 'BOTH') {
      if (!quantityValue.trim()) {
        newErrors.quantityValue = 'Quantity is required';
      }

      if (!financialValue.trim()) {
        newErrors.financialValue = 'Financial is required';
      }

      const quantityChanged = quantityValue.toString() !== initialQuantityValue?.toString();
      const financialChanged = financialValue.toString() !== initialFinancialValue?.toString();

      if (
        !quantityChanged &&
        !financialChanged &&
        !newErrors.quantityValue &&
        !newErrors.financialValue
      ) {
        newErrors.quantityValue = 'Please change at least one value';
      }
    } else if (estimationType === 'FINANCIAL') {
      if (!financialValue.trim()) {
        newErrors.financialValue = 'This field is required';
      } else if (financialValue.toString() === initialFinancialValue?.toString()) {
        newErrors.financialValue = 'Please change the value before submitting';
      }
    } else {
      if (!quantityValue.trim()) {
        newErrors.quantityValue = 'This field is required';
      } else if (quantityValue.toString() === initialQuantityValue?.toString()) {
        newErrors.quantityValue = 'Please change the value before submitting';
      }
    }

    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }

    onSubmit?.({
      quantityValue,
      financialValue,
      comment,
    });

    handleClose();
  };

  const handleClose = () => {
    setQuantityValue('');
    setFinancialValue('');
    setComment('');
    setErrors({});
    onClose?.();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Presented Value"
      width="448px"
      $centered
      footer={false}
    >
      <FormWrapper>
        {estimationType === 'BOTH' ? (
          <DualInputWrapper>
            <InputSection>
              <Input
                label="Quantity"
                required
                placeholder="Fill in the quantity"
                value={quantityValue}
                onChange={(e) => {
                  setQuantityValue(e.target.value);
                  setErrors((prev) => ({ ...prev, quantityValue: null }));
                }}
                error={!!errors.quantityValue}
                fullWidth
              />
              {errors.quantityValue && (
                <ErrorForInputs>
                  <AlertIcon src={VectorIcon} />
                  <ErrorMessage>{errors.quantityValue}</ErrorMessage>
                </ErrorForInputs>
              )}
            </InputSection>

            <InputSection>
              <Input
                label="Financial"
                required
                placeholder="Fill in the financial"
                value={financialValue}
                onChange={(e) => {
                  setFinancialValue(e.target.value);
                  setErrors((prev) => ({ ...prev, financialValue: null }));
                }}
                error={!!errors.financialValue}
                fullWidth
              />
              {errors.financialValue && (
                <ErrorForInputs>
                  <AlertIcon src={VectorIcon} />
                  <ErrorMessage>{errors.financialValue}</ErrorMessage>
                </ErrorForInputs>
              )}
            </InputSection>
          </DualInputWrapper>
        ) : (
          <div>
            <Input
              label={estimationType === 'FINANCIAL' ? 'Financial' : 'Quantity'}
              required
              placeholder={`Fill in the ${estimationType === 'FINANCIAL' ? 'financial' : 'quantity'} value`}
              value={estimationType === 'FINANCIAL' ? financialValue : quantityValue}
              onChange={(e) => {
                if (estimationType === 'FINANCIAL') {
                  setFinancialValue(e.target.value);
                  setErrors((prev) => ({ ...prev, financialValue: null }));
                } else {
                  setQuantityValue(e.target.value);
                  setErrors((prev) => ({ ...prev, quantityValue: null }));
                }
              }}
              error={
                estimationType === 'FINANCIAL'
                  ? !!errors.financialValue
                  : !!errors.quantityValue
              }
              fullWidth
            />

            {(errors.quantityValue || errors.financialValue) && (
              <ErrorForInputs>
                <AlertIcon src={VectorIcon} />
                <ErrorMessage>
                  {estimationType === 'FINANCIAL'
                    ? errors.financialValue
                    : errors.quantityValue}
                </ErrorMessage>
              </ErrorForInputs>
            )}
          </div>
        )}

        <TextArea
          label="Comment"
          placeholder="Add comment"
          tooltip="Add comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          maxLength={250}
          resizeHorizontal={false}
        />

        <ButtonWrapper>
          <Button outlined onClick={handleClose} width="45%">
            Cancel
          </Button>
          <Button primary onClick={handleSubmit} width="50%" loading={loading}>
            Submit
          </Button>
        </ButtonWrapper>
      </FormWrapper>
    </Modal>
  );
};

export default EditPresValue;
